import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import Button from './Button'; // Ensure Button component is imported

/**
 * A reusable Modal component with a modern design and animation.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.isOpen - Controls the visibility of the modal.
 * @param {function} props.onClose - Callback function when the modal is requested to close.
 * @param {string} [props.title=''] - Title displayed at the top of the modal.
 * @param {React.ReactNode} props.children - Content to be displayed inside the modal body.
 * @param {string} [props.className=''] - Additional CSS classes for the modal content container.
 * @param {string} [props.size='md'] - Predefined modal sizes: 'sm', 'md', 'lg', 'xl'.
 */
const Modal = ({ isOpen, onClose, title = '', children, className = '', size = 'md' }) => {
    const modalRef = useRef(null);

    // Close modal on escape key press
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden'; // Prevent scrolling background
        } else {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    // Removed handleOverlayClick to prevent closing on click outside

    const getSizeClasses = () => {
        switch (size) {
            case 'sm': return 'max-w-sm';
            case 'md': return 'max-w-md';
            case 'lg': return 'max-w-lg';
            case 'xl': return 'max-w-xl';
            case 'wide': return 'max-w-4xl'; // Added 'wide' size for larger modals
            default: return 'max-w-md';
        }
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.9, y: -50 },
        visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
        exit: { opacity: 0, scale: 0.9, y: -50, transition: { duration: 0.15, ease: "easeIn" } },
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
                // Removed onClick={handleOverlayClick} from here
                >
                    <motion.div
                        ref={modalRef}
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className={`bg-white rounded-lg shadow-2xl w-full ${getSizeClasses()} max-h-[90vh] overflow-hidden flex flex-col ${className}`}
                        onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
                    >
                        {/* Modal Header */}
                        <div className="flex justify-between items-center p-5 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-text-primary">
                                {title}
                            </h3>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onClose}
                                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                                aria-label="Close modal"
                            >
                                <X size={20} />
                            </Button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-5 overflow-y-auto flex-1">
                            {children}
                        </div>
                        {/* Footer can be added here if needed, e.g., for action buttons */}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Modal;
